import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PaywallStateName = "free" | "grace" | "paywalled" | "paid";

export interface PaywallSnapshot {
  state: PaywallStateName;
  tier: string;
  held_meetings: number;
  first_held_at: string | null;
  grace_ends_at: string | null;
  grace_seconds_remaining: number | null;
  can_send: boolean;
  meeting_allowance: number;
  is_beta: boolean;
}

const DEFAULT_SNAPSHOT: PaywallSnapshot = {
  state: "free",
  tier: "free",
  held_meetings: 0,
  first_held_at: null,
  grace_ends_at: null,
  grace_seconds_remaining: null,
  can_send: true,
  meeting_allowance: 1,
  is_beta: false,
};

/**
 * The single source of truth for whether outbound should run and whether
 * the user has hit the meeting paywall.
 *
 * Replaces the old credit-based enforcement.
 */
export function usePaywallState(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["paywall-state", tenantId],
    queryFn: async (): Promise<PaywallSnapshot> => {
      if (!tenantId) return DEFAULT_SNAPSHOT;
      const { data, error } = await supabase.rpc("get_paywall_state", {
        _tenant_id: tenantId,
      });
      if (error) {
        // Fail open — never lock a user out because of a transient RPC issue.
        return DEFAULT_SNAPSHOT;
      }
      return data as unknown as PaywallSnapshot;
    },
    enabled: !!tenantId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/** Format seconds remaining in the grace window into a friendly "Xd Yh" / "Xh Ym" string. */
export function formatGraceRemaining(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || seconds <= 0) return null;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
